// @ts-nocheck
/*
PIXEL ART CREATOR
Marion Walton, University of Cape Town
Creative Code
http://ikamvacodes.wordpress.com

Converted to p5.js and further adapted for use as a bitmap editor by
Stephen Wolff, Max Gate Digital Ltd

Converted to typescript and further adapted for use as a bitmap editor in React by
Michael Pretorius, Younglings Digital Studios
*/

import { Sketch, P5CanvasInstance } from '@p5-wrapper/react';
// import { toast } from 'react-toastify';

type Colour = any;
type Image = any;

// PARAMETERS
export interface Parameters {
	width: number;
	height: number;
	snapshot?: string;
	background_image?: string;
	background_grayscale?: number; // 0-100
	background_opacity?: number; // 0-1
	bgcolour?: string;
	onColorUsed?: (color: string) => void; // Callback when a color is actually used for drawing
}
let parameters = {
	width: 100,
	height: 100,
	snapshot: undefined,
	background_image: undefined,
	background_grayscale: 0,
	background_opacity: 1,
	bgcolour: undefined,
	onColorUsed: undefined,
};
export const setParameters = (params: Parameters) => {
	const oldParams = { ...parameters };
	parameters = params;

	// Reload background image in both sketches if it changed
	if (oldParams.background_image !== params.background_image) {
		// Trigger background image reload in both sketches
		if (window?.editSketch?.reloadBackgroundImage) {
			window.editSketch.reloadBackgroundImage(params.background_image);
		}
		if (window?.displaySketch?.reloadBackgroundImage) {
			window.displaySketch.reloadBackgroundImage(params.background_image);
		}
	}
};
export const getParameters = () => parameters;

let canvasDimensions = {
	width: 400,
	height: 400,
};

export const setCanvasDimensions = (width: number, height: number) => {
	canvasDimensions.width = width;
	canvasDimensions.height = height;
};

// GRID SETTINGS
let gridSettings = {
	enabled: false,
	opacity: 0.45, // Visibility of grid lines (higher because lines are sparse)
	interval: 10, // Draw a grid line every N canvas pixels
};

export const setGridEnabled = (enabled: boolean) => {
	gridSettings.enabled = enabled;
};

export const getGridEnabled = () => gridSettings.enabled;

export const setGridInterval = (interval: number) => {
	gridSettings.interval = Math.max(1, Math.floor(interval));
};

export const getGridInterval = () => gridSettings.interval;

// HISTORY MANAGEMENT
const maxHistorySteps = 50;
let undoStack: Colour[][][] = [];
let redoStack: Colour[][][] = [];

const saveToHistory = (currentColours: Colour[][], source: string = 'unknown') => {
	// Deep clone the colours array to avoid reference issues
	const coloursCopy = currentColours.map(row => row.map(col => col));
	undoStack.push(coloursCopy);

	// Keep history within limits
	if (undoStack.length > maxHistorySteps) {
		undoStack.shift();
	}

	// Clear redo stack when new action is made
	redoStack = [];
};

export const canUndo = () => {
	// We need at least 2 entries: initial state + at least one change
	const result = undoStack.length > 1;
	return result;
};
export const canRedo = () => {
	const result = redoStack.length > 0;
	return result;
};

let undoRedoInProgress = false;
let lastUndoRedoTime = 0;

export const performUndo = () => {
	if (undoStack.length > 1 && window.editSketch) {
		const p = window.editSketch;

		// Set flag to prevent drawing during undo
		undoRedoInProgress = true;
		lastUndoRedoTime = Date.now();

		// Save current state to redo stack
		const currentColours = p.getColours();
		const coloursCopy = currentColours.map((row: Colour[]) => row.map((col: Colour) => col));
		redoStack.push(coloursCopy);

		// Restore previous state
		const previousState = undoStack.pop();
		if (previousState) {
			p.setColours(previousState);
		}

		// Clear flag after ensuring no mouse interaction for sufficient time
		let protectionChecks = 0;
		const maxProtectionChecks = 5; // Check up to 5 times (500ms total)

		const clearProtection = () => {
			protectionChecks++;
			if (p.mouseIsPressed || p.touchIsDown || protectionChecks < maxProtectionChecks) {
				if (p.mouseIsPressed || p.touchIsDown) {
					protectionChecks = 0; // Reset counter if mouse is still pressed
				} else {
					// No changes, so don't save to history
				}
				setTimeout(clearProtection, 100);
			} else {
				undoRedoInProgress = false;
			}
		};
		setTimeout(clearProtection, 150); // Slightly longer initial delay

		return true;
	}
	return false;
};

export const performRedo = () => {
	if (canRedo() && window.editSketch) {
		const p = window.editSketch;

		// Set flag to prevent drawing during redo
		undoRedoInProgress = true;
		lastUndoRedoTime = Date.now();

		// Save current state to undo stack
		const currentColours = p.getColours();
		const coloursCopy = currentColours.map((row: Colour[]) => row.map((col: Colour) => col));
		undoStack.push(coloursCopy);

		// Restore next state
		const nextState = redoStack.pop();
		if (nextState) {
			p.setColours(nextState);
		}

		// Clear flag after ensuring no mouse interaction for sufficient time
		let protectionChecks = 0;
		const maxProtectionChecks = 5; // Check up to 5 times (500ms total)

		const clearProtection = () => {
			protectionChecks++;
			if (p.mouseIsPressed || p.touchIsDown || protectionChecks < maxProtectionChecks) {
				if (p.mouseIsPressed || p.touchIsDown) {
					protectionChecks = 0; // Reset counter if mouse is still pressed
				} else {
					// No changes, so don't save to history
				}
				setTimeout(clearProtection, 100);
			} else {
				undoRedoInProgress = false;
			}
		};
		setTimeout(clearProtection, 150); // Slightly longer initial delay

		return true;
	}
	return false;
};

export const editSketch: Sketch = function (p: P5CanvasInstance) {
	// const debug = false;
	let img: Image | undefined;
	let backgroundImg: Image | undefined;

	// set up array of beads
	const rows = parameters.height;
	const columns = parameters.width;

	let backgroundColour: Colour = p.color(0);
	let pencilColour: Colour = p.color(255);

	// set the drawing size in pixels for the pixels
	let drawSize = 10; // num pixels width and height per pixel
	let pencilSizeX = 1;
	let pencilSizeY = 1;
	const maxSize = 20;
	const minSize = 1;

	p.canvasWidth = canvasDimensions.width;
	p.canvasHeight = canvasDimensions.height;
	p.canvasCentreX = p.canvasWidth / 2;
	p.canvasCentreY = p.canvasHeight / 2;
	p.viewCenterX = p.canvasCentreX;
	p.viewCenterY = p.canvasCentreY;
	let screenXAdjust: number;
	let screenYAdjust: number;

	let colours: Colour[][] = [];
	const frameColour: Colour = p.color(80);

	let mouseStartDragX = -1;
	let mouseStartDragY = -1;
	p.dragStart = false;
	p.DRAW_MODE = 1;
	p.HAND_MODE = 2;
	p.BUCKET_MODE = 3;
	p.SHAPE_MODE = 4;
	p.toolMode = p.DRAW_MODE;
	// Bucket fill: false = contiguous (connected area), true = global (all matching cells)
	let fillGlobal = false;
	// Shapes tool: drag from a start cell to an end cell, live preview, commit on release
	let shapeType: 'line' | 'rect' | 'ellipse' = 'line';
	let shapeFilled = false;
	let shapeSnap = false; // snap lines to 45° increments
	let shapeDragging = false;
	let shapeStartCol = -1;
	let shapeStartRow = -1;
	// Pencil strokes must START inside the bitmap (a press on a toolbar button
	// that later crosses the canvas must not paint)
	let pencilStroking = false;

	// Remount cycles (e.g. the initial-load spinner) can leave a stale p5
	// instance whose window-level listeners keep firing with coordinates
	// relative to its detached (0,0) canvas — painting phantom cells and
	// corrupting the shared undo history. Only the most recently registered
	// instance may handle input, paint, or touch history.
	const isActiveInstance = () => (window as any).editSketch === p;

	p.preload = () => {
		if (parameters.snapshot !== undefined && parameters.snapshot !== null) {
			img = p.loadImage(parameters.snapshot);
		}
		if (parameters.background_image !== undefined && parameters.background_image !== null) {
			backgroundImg = p.loadImage(parameters.background_image);
		}
	};

	p.setup = () => {
		p.createCanvas(p.canvasWidth, p.canvasHeight);
		p.frameRate(30);
		p.updateScreenAdjust();

		p.toolMode = p.DRAW_MODE;

		if (img) {
			p.loadPixels();

			// We must also call loadPixels() on the PImage since we are going to read its pixels.
			img.loadPixels();
		}

		for (var i = 0; i < rows; i++) {
			colours[i] = [];
			for (var j = 0; j < columns; j++) {
				if (img !== undefined) {
					var loc = (j + i * img.width) * 4;

					// Get the R,G,B values from image
					var r = img.pixels[loc];
					var g = img.pixels[loc + 1];
					var b = img.pixels[loc + 2];

					if (r !== undefined) {
						colours[i][j] = p.color(r, g, b);
					} else {
						colours[i][j] = undefined;
					}
				} else {
					colours[i][j] = undefined;
					// use transparent?
					//p.color(0, 0, 0, 255);
				}
			}
		}

		// Clear any existing history and save clean initial state
		undoStack = []; // Clear any existing history
		redoStack = []; // Clear redo stack
		saveToHistory(colours, 'initial-setup');
	};

	p.draw = () => {
		if (!isActiveInstance()) return;
		p.smooth();

		// Clear the canvas first to ensure transparency works correctly
		p.clear();

		//draw background
		p.background(backgroundColour);

		// show background image within square
		if (backgroundImg) {
			// Apply grayscale and opacity filters
			p.push();
			if (parameters.background_grayscale && parameters.background_grayscale > 0) {
				// Apply grayscale filter using tint
				const grayValue = 255 - (parameters.background_grayscale / 100) * 255;
				p.tint(grayValue, grayValue, grayValue, (parameters.background_opacity || 1) * 255);
			} else {
				// Apply only opacity
				p.tint(255, 255, 255, (parameters.background_opacity || 1) * 255);
			}
			// Displays the image at point (0, height/2) at half size
			p.image(backgroundImg, getScreenX_Left(0), getScreenY_Top(0), columns * drawSize, rows * drawSize);
			p.pop();
		}

		window?.displaySketch?.updateColours(colours, backgroundColour);

		//use 'for' loop to draw rows down the y axis
		for (var i = 0; i < rows; i++) {
			for (var j = 0; j < columns; j++) {
				if (colours[i][j] !== undefined) {
					drawBead(j, i, colours[i][j]);
				}
			}
		}

		// mousePressed replaced with mouseIsPressed
		if (p.mouseIsPressed || p.touchIsDown) {
			// toast(`p.draw mouseIsPressed ${p.toolMode} ${p.dragStart}`, { closeOnClick: true, autoClose: 1000, toastId: 'p.drawmouseIsPressed' });
			if (p.toolMode === p.DRAW_MODE && pencilStroking) {
				if (p.mouseButton === p.LEFT) {
					// Only allow drawing if mouse is within the canvas bounds
					const canvasLeft = getScreenX_Left(0);
					const canvasTop = getScreenY_Top(0);
					const canvasRight = canvasLeft + columns * drawSize;
					const canvasBottom = canvasTop + rows * drawSize;

					if (p.mouseX >= canvasLeft && p.mouseX <= canvasRight && p.mouseY >= canvasTop && p.mouseY <= canvasBottom) {
						setColour();
					} else {
						// Mouse is outside the canvas bounds, so don't draw
					}
				}
			}

			if (p.toolMode === p.HAND_MODE) {
				if (p.dragStart) {
					if (p.mouseX > 0 && p.mouseX < p.canvasWidth && p.mouseY > 0 && p.mouseY < p.canvasHeight) {
						// toast(`p.draw move`, { closeOnClick: true, autoClose: 1000, toastId: 'p.drawmove' });
						p.viewCenterX -= mouseStartDragX - p.mouseX;
						p.viewCenterY -= mouseStartDragY - p.mouseY;
						mouseStartDragX = p.mouseX;
						mouseStartDragY = p.mouseY;
						p.updateScreenAdjust();
					}
				}
			}
		}

		// live shape preview while dragging (rubber-band, committed on release)
		if (p.toolMode === p.SHAPE_MODE && shapeDragging) {
			const [endCol, endRow] = currentShapeEnd();
			computeShapeCells(shapeStartCol, shapeStartRow, endCol, endRow).forEach(idx => {
				const r = (idx / columns) | 0;
				drawBead(idx - r * columns, r, pencilColour);
			});
		}

		// draw grid if enabled
		if (gridSettings.enabled) {
			drawGrid();
		}

		// draw frame
		p.noFill();
		p.stroke(frameColour);
		p.rect(getScreenX_Left(0), getScreenY_Top(0), columns * drawSize, rows * drawSize);

		// draw pencil
		if (p.toolMode === p.DRAW_MODE) {
			drawPencil();
		}
	};

	p.touchStarted = () => {
		// toast(`p.touchStarted, ${p.toolMode}`, { closeOnClick: true, autoClose: 1000 });
		mousePressed();
	};
	p.mousePressed = () => {
		// toast(`p.mousePressed, ${p.toolMode}`, { closeOnClick: true, autoClose: 1000 });
		mousePressed();
	};

	const mousePressed = () => {
		if (!isActiveInstance()) return;
		const insideBitmap = () => {
			const canvasLeft = getScreenX_Left(0);
			const canvasTop = getScreenY_Top(0);
			return (
				p.mouseX >= canvasLeft &&
				p.mouseX <= canvasLeft + columns * drawSize &&
				p.mouseY >= canvasTop &&
				p.mouseY <= canvasTop + rows * drawSize
			);
		};
		if (p.toolMode === p.DRAW_MODE) {
			// A pencil stroke only starts when the press lands on the bitmap
			pencilStroking = insideBitmap();
			return;
		}
		if (p.toolMode === p.BUCKET_MODE) {
			// Bucket fills on a single tap (not on drag)
			bucketFill();
			return;
		}
		if (p.toolMode === p.SHAPE_MODE) {
			if (undoRedoInProgress || Date.now() - lastUndoRedoTime < 750) return;
			// Only start a shape when pressing inside the bitmap
			if (insideBitmap()) {
				shapeStartCol = clampCol(getArrayX(p.mouseX));
				shapeStartRow = clampRow(getArrayY(p.mouseY));
				shapeDragging = true;
			}
			return;
		}
		if (p.toolMode === p.HAND_MODE) {
			if (p.mouseX > 0 && p.mouseX < p.canvasWidth && p.mouseY > 0 && p.mouseY < p.canvasHeight && p.dragStart === false) {
				// toast(`p.mousePressed, mark start hand move`, { closeOnClick: true, autoClose: 1000 });
				// mark start hand move
				mouseStartDragX = p.mouseX;
				mouseStartDragY = p.mouseY;
				p.dragStart = true;
			}
		}
	};

	p.mouseReleased = () => {
		// toast(`mouseReleased`, { closeOnClick: true, autoClose: 1000 });
		if (!isActiveInstance()) return;
		pencilStroking = false;
		if (p.toolMode === p.HAND_MODE && p.dragStart) {
			resetMouseDrag();
		}

		// Commit the dragged shape to the bitmap (one undo reverts the whole shape)
		if (p.toolMode === p.SHAPE_MODE && shapeDragging) {
			shapeDragging = false;
			const [endCol, endRow] = currentShapeEnd();
			const cells = computeShapeCells(shapeStartCol, shapeStartRow, endCol, endRow);
			if (cells.size > 0) {
				const snapshot = colours.map(row => row.map(col => col));
				let changed = false;
				cells.forEach(idx => {
					const r = (idx / columns) | 0;
					const c = idx - r * columns;
					if (colours[r][c] !== pencilColour) {
						colours[r][c] = pencilColour;
						changed = true;
					}
				});
				if (changed) {
					if (parameters.onColorUsed) {
						parameters.onColorUsed(rgbaToHexa(pencilColour.toString()));
					}
					saveToHistory(snapshot, 'shape-action');
					if (window?.displaySketch?.updateColours) {
						window.displaySketch.updateColours(colours, backgroundColour);
					}
				}
			}
		}

		// Check if drawing actually changed anything and save to history if it did
		if (drawingInProgress && preDrawingState) {
			let hasChanges = false;

			// Compare current state with pre-drawing state
			for (let i = 0; i < rows && !hasChanges; i++) {
				for (let j = 0; j < columns && !hasChanges; j++) {
					if (colours[i][j] !== preDrawingState[i][j]) {
						hasChanges = true;
					}
				}
			}

			if (hasChanges) {
				saveToHistory(preDrawingState, 'drawing-action'); // Save the state BEFORE the changes
			} else {
				// No changes, so don't save to history
			}

			preDrawingState = null;
		}

		// Reset drawing state so next action captures a new pre-drawing state
		drawingInProgress = false;
	};

	// ZOOM helpers
	// ========================================
	/**
	 * zoom can be +1 or -1
	 */
	p.updateZoom = (zoom: number) => {
		if (drawSize + zoom <= maxSize && drawSize + zoom >= minSize) {
			drawSize = drawSize + zoom;
			p.updateScreenAdjust();
		}
	};

	// BACKGROUND helpers
	// ========================================
	p.setBackgroundColour = (colour: Colour) => {
		// Directly use the color value which preserves the alpha channel
		backgroundColour = p.color(colour);
	};
	p.getBackgroundColour = () => backgroundColour;
	p.getBackgroundColourHex = () => rgbaToHexa(backgroundColour.toString());

	p.setBackgroundImage = (imageUrl: string) => {};

	// PENCIL helpers
	// ========================================
	p.setPencilColour = (colour: Colour) => {
		// Directly use the color value which preserves the alpha channel
		pencilColour = p.color(colour);
	};
	p.getPencilColour = () => pencilColour;
	p.getPencilColourHex = () => rgbaToHexa(pencilColour.toString());

	p.getPensilSize = (): number => ({ x: pencilSizeX, y: pencilSizeY });
	p.setPensilSize = (width: number, height: number) => {
		pencilSizeX = width;
		pencilSizeY = height;
	};

	p.toggleMode = () => {
		if (p.toolMode === p.DRAW_MODE) {
			p.toolMode = p.HAND_MODE;
			p.cursor(p.HAND);
		} else {
			p.toolMode = p.DRAW_MODE;
			p.cursor(p.ARROW);
			resetMouseDrag();
		}
	};

	// BUCKET (fill) helpers
	// ========================================
	p.setBucketActive = (active: boolean) => {
		if (active) {
			p.toolMode = p.BUCKET_MODE;
			resetMouseDrag();
			p.cursor(p.CROSS);
		} else if (p.toolMode === p.BUCKET_MODE) {
			p.toolMode = p.DRAW_MODE;
			p.cursor(p.ARROW);
		}
	};
	p.setFillGlobal = (global: boolean) => {
		fillGlobal = !!global;
	};
	p.getFillGlobal = () => fillGlobal;

	// SHAPES helpers
	// ========================================
	p.setShapeActive = (active: boolean) => {
		if (active) {
			p.toolMode = p.SHAPE_MODE;
			resetMouseDrag();
			p.cursor(p.CROSS);
		} else if (p.toolMode === p.SHAPE_MODE) {
			p.toolMode = p.DRAW_MODE;
			p.cursor(p.ARROW);
			shapeDragging = false;
		}
	};
	p.setShapeType = (type: 'line' | 'rect' | 'ellipse') => {
		shapeType = type;
	};
	p.getShapeType = () => shapeType;
	p.setShapeFilled = (filled: boolean) => {
		shapeFilled = !!filled;
	};
	p.getShapeFilled = () => shapeFilled;
	p.setShapeSnap = (snap: boolean) => {
		shapeSnap = !!snap;
	};
	p.getShapeSnap = () => shapeSnap;

	// DRAWSIZE helpers
	// ========================================
	p.getDrawSize = (): number => {
		return drawSize;
	};
	p.setDrawSize = (newSize: number) => {
		drawSize = newSize;
	};

	p.getZoomPercentage = (): number => {
		// Zoom percentage relative to actual bitmap size:
		// drawSize 1 = 0% zoom (actual size)
		// drawSize 2 = 100% zoom (2x actual size)
		// drawSize 3 = 200% zoom (3x actual size)
		return Math.round((drawSize - 1) * 100);
	};

	// HISTORY METHODS
	p.getColours = (): Colour[][] => {
		return colours;
	};

	p.setColours = (newColours: Colour[][]) => {
		// Copy contents element by element instead of reassigning reference
		for (let i = 0; i < rows; i++) {
			for (let j = 0; j < columns; j++) {
				if (newColours[i] && newColours[i][j] !== undefined) {
					colours[i][j] = newColours[i][j];
				} else {
					colours[i][j] = undefined;
				}
			}
		}

		// Update the display sketch as well
		if (window?.displaySketch?.updateColours) {
			window.displaySketch.updateColours(colours, backgroundColour);
		}

		// Force redraw
	};

	p.updateScreenAdjust = () => {
		screenXAdjust = p.viewCenterX - (columns / 2) * drawSize;
		screenYAdjust = p.viewCenterY - (rows / 2) * drawSize;
	};

	function resetMouseDrag() {
		// toast(`resetMouseDrag`, { closeOnClick: true, autoClose: 1000 });
		p.dragStart = false;
		mouseStartDragX = -1;
		mouseStartDragY = -1;
	}

	function getScreenX_Left(x: number) {
		return x * drawSize + screenXAdjust;
	}

	function getArrayX(screenX_Left: number) {
		return p.int((screenX_Left - screenXAdjust) / drawSize);
	}

	function getScreenY_Top(y: number) {
		var screenY = y * drawSize + screenYAdjust;
		if (mouseStartDragX !== -1 && mouseStartDragY !== -1) {
		}
		return screenY;
	}

	function getArrayY(screenY_Top: number) {
		return p.int((screenY_Top - screenYAdjust) / drawSize);
	}

	function drawBead(colvalue: number, rowvalue: number, beadColour: Colour) {
		var screenX = getScreenX_Left(colvalue);
		var screenY = getScreenY_Top(rowvalue);

		p.fill(beadColour);
		p.noStroke();

		p.rect(screenX, screenY, drawSize, drawSize);
	}

	function drawGrid() {
		// Set grid color - light gray with opacity
		p.stroke(p.color(128, 128, 128, 255 * gridSettings.opacity));
		p.strokeWeight(1);

		const startX = getScreenX_Left(0);
		const startY = getScreenY_Top(0);
		const gridWidth = columns * drawSize;
		const gridHeight = rows * drawSize;
		const interval = Math.max(1, gridSettings.interval);

		// Vertical lines at chosen interval
		for (let i = 0; i <= columns; i += interval) {
			const x = startX + i * drawSize;
			p.line(x, startY, x, startY + gridHeight);
		}
		// Ensure the right edge is always drawn even if interval doesn't divide evenly
		if (columns % interval !== 0) {
			const x = startX + columns * drawSize;
			p.line(x, startY, x, startY + gridHeight);
		}

		// Horizontal lines at chosen interval
		for (let i = 0; i <= rows; i += interval) {
			const y = startY + i * drawSize;
			p.line(startX, y, startX + gridWidth, y);
		}
		if (rows % interval !== 0) {
			const y = startY + rows * drawSize;
			p.line(startX, y, startX + gridWidth, y);
		}
	}

	function drawPencil() {
		var rowvalue = getArrayY(p.mouseY);
		var colvalue = getArrayX(p.mouseX);

		// inside bitmap size?
		for (var beadX = 0; beadX < pencilSizeX; beadX++) {
			for (var beadY = 0; beadY < pencilSizeY; beadY++) {
				if (colvalue >= 0 && colvalue + beadX < columns && rowvalue >= 0 && rowvalue + beadY < rows) {
					var screenX = getScreenX_Left(colvalue + beadX);
					var screenY = getScreenY_Top(rowvalue + beadY);

					// inside visible area?
					if (screenX >= 0 && screenX < p.width && screenY >= 0 && screenY < p.height) {
						if (beadX === 0 && beadY === 0) {
							drawPencilDottedRect(screenX, screenY);
						}
					}
				}
			}
		}
	}

	function drawPencilDottedRect(_x: number, _y: number) {
		for (var x = 0; x <= drawSize * pencilSizeX; x += 3) {
			var x1 = _x + x;
			var y1 = _y;
			var y2 = _y + drawSize * pencilSizeY;
			p.point(x1, y1);
			p.point(x1, y2);
		}

		for (var y = 0; y <= drawSize * pencilSizeY; y += 3) {
			var x3 = _x;
			var x4 = _x + drawSize * pencilSizeX;
			var y3 = _y + y;
			p.point(x3, y3);
			p.point(x4, y3);
		}
	}

	let drawingInProgress = false;
	let preDrawingState: Colour[][] | null = null;

	function setColour() {
		// Prevent drawing during undo/redo operations
		if (undoRedoInProgress) {
			return;
		}

		// Additional protection: prevent drawing for 750ms after undo/redo
		const now = Date.now();
		const timeSinceUndoRedo = now - lastUndoRedoTime;
		if (timeSinceUndoRedo < 750) {
			return;
		}

		var rowvalue = getArrayY(p.mouseY);
		var colvalue = getArrayX(p.mouseX);

		// Save current state before first change in a drawing action
		if (!drawingInProgress) {
			preDrawingState = colours.map(row => row.map(col => col)); // Deep clone
			drawingInProgress = true;

			// Notify that a color is being used for drawing
			if (parameters.onColorUsed) {
				const currentColorHex = rgbaToHexa(pencilColour.toString());
				parameters.onColorUsed(currentColorHex);
			}
		}

		// inside bitmap size?
		for (var beadX = 0; beadX < pencilSizeX; beadX++) {
			for (var beadY = 0; beadY < pencilSizeY; beadY++) {
				if (colvalue >= 0 && colvalue + beadX < columns && rowvalue >= 0 && rowvalue + beadY < rows) {
					var screenX = getScreenX_Left(colvalue + beadX);
					var screenY = getScreenY_Top(rowvalue + beadY);

					// inside visible area?
					if (screenX >= 0 && screenX < p.width && screenY >= 0 && screenY < p.height) {
						colours[rowvalue + beadY][colvalue + beadX] = pencilColour;
					}
				}
			}
		}
	}

	// Exact colour equality for two grid cells (undefined = empty/unpainted cell).
	function coloursEqual(a: Colour, b: Colour) {
		const aEmpty = a === undefined || a === null;
		const bEmpty = b === undefined || b === null;
		if (aEmpty || bEmpty) return aEmpty && bEmpty;
		const la = a.levels;
		const lb = b.levels;
		if (la && lb) {
			return la[0] === lb[0] && la[1] === lb[1] && la[2] === lb[2] && la[3] === lb[3];
		}
		return a.toString() === b.toString();
	}

	// Paint-bucket flood fill. Contiguous (4-connected) by default, or global
	// (every matching cell on the canvas) when fillGlobal is true. Exact colour match.
	function bucketFill() {
		// Respect the same undo/redo guards as normal drawing
		if (undoRedoInProgress) return;
		if (Date.now() - lastUndoRedoTime < 750) return;

		const startCol = getArrayX(p.mouseX);
		const startRow = getArrayY(p.mouseY);

		// Only fill when the tap is inside the bitmap
		if (startCol < 0 || startCol >= columns || startRow < 0 || startRow >= rows) return;

		const targetColour = colours[startRow][startCol];
		const newColour = pencilColour;

		// No-op if the tapped area already matches the fill colour
		if (coloursEqual(targetColour, newColour)) return;

		// Snapshot BEFORE changes so a single undo reverts the whole fill
		const snapshot = colours.map(row => row.map(col => col));

		let changed = false;

		if (fillGlobal) {
			// Replace every matching cell across the whole grid
			for (let i = 0; i < rows; i++) {
				for (let j = 0; j < columns; j++) {
					if (coloursEqual(colours[i][j], targetColour)) {
						colours[i][j] = newColour;
						changed = true;
					}
				}
			}
		} else {
			// Contiguous 4-connected flood fill (iterative stack + typed visited set)
			const visited = new Uint8Array(rows * columns);
			const stack: number[] = [startRow * columns + startCol];
			while (stack.length > 0) {
				const idx = stack.pop() as number;
				if (visited[idx]) continue;
				visited[idx] = 1;
				const r = (idx / columns) | 0;
				const c = idx - r * columns;
				if (!coloursEqual(colours[r][c], targetColour)) continue;
				colours[r][c] = newColour;
				changed = true;
				if (c + 1 < columns) stack.push(idx + 1);
				if (c - 1 >= 0) stack.push(idx - 1);
				if (r + 1 < rows) stack.push(idx + columns);
				if (r - 1 >= 0) stack.push(idx - columns);
			}
		}

		if (changed) {
			// Feed the recent-colours history, same as the pencil
			if (parameters.onColorUsed) {
				parameters.onColorUsed(rgbaToHexa(pencilColour.toString()));
			}
			saveToHistory(snapshot, 'bucket-fill');
			// Push immediately to the display sketch so any preview stays in sync
			if (window?.displaySketch?.updateColours) {
				window.displaySketch.updateColours(colours, backgroundColour);
			}
		}
	}

	// SHAPE GEOMETRY
	// ========================================
	function clampCol(c: number) {
		return Math.min(Math.max(0, c), columns - 1);
	}
	function clampRow(r: number) {
		return Math.min(Math.max(0, r), rows - 1);
	}

	// Current drag end cell, optionally snapped to 45° increments for lines
	function currentShapeEnd() {
		let endCol = clampCol(getArrayX(p.mouseX));
		let endRow = clampRow(getArrayY(p.mouseY));
		if (shapeType === 'line' && shapeSnap) {
			const dx = endCol - shapeStartCol;
			const dy = endRow - shapeStartRow;
			if (dx !== 0 || dy !== 0) {
				const snapped = Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) * (Math.PI / 4);
				const len = Math.max(Math.abs(dx), Math.abs(dy));
				endCol = clampCol(shapeStartCol + Math.round(Math.cos(snapped)) * len);
				endRow = clampRow(shapeStartRow + Math.round(Math.sin(snapped)) * len);
			}
		}
		return [endCol, endRow];
	}

	// Bresenham line between two cells
	function lineCells(c0: number, r0: number, c1: number, r1: number, visit: (c: number, r: number) => void) {
		const dx = Math.abs(c1 - c0);
		const sx = c0 < c1 ? 1 : -1;
		const dy = -Math.abs(r1 - r0);
		const sy = r0 < r1 ? 1 : -1;
		let err = dx + dy;
		let c = c0;
		let r = r0;
		for (;;) {
			visit(c, r);
			if (c === c1 && r === r1) break;
			const e2 = 2 * err;
			if (e2 >= dy) {
				err += dy;
				c += sx;
			}
			if (e2 <= dx) {
				err += dx;
				r += sy;
			}
		}
	}

	// Cells covered by the current shape between two corners, as a Set of
	// r*columns+c indices. Lines and outlines are stamped with the pencil size
	// so the pencil's thickness applies to shapes too; filled shapes cover
	// their exact interior.
	function computeShapeCells(c0: number, r0: number, c1: number, r1: number) {
		const cells = new Set<number>();
		const stamp = (c: number, r: number) => {
			for (let bx = 0; bx < pencilSizeX; bx++) {
				for (let by = 0; by < pencilSizeY; by++) {
					const cc = c + bx;
					const rr = r + by;
					if (cc >= 0 && cc < columns && rr >= 0 && rr < rows) {
						cells.add(rr * columns + cc);
					}
				}
			}
		};

		if (shapeType === 'line') {
			lineCells(c0, r0, c1, r1, stamp);
			return cells;
		}

		const left = Math.min(c0, c1);
		const right = Math.max(c0, c1);
		const top = Math.min(r0, r1);
		const bottom = Math.max(r0, r1);

		if (shapeType === 'rect') {
			for (let r = top; r <= bottom; r++) {
				for (let c = left; c <= right; c++) {
					if (shapeFilled) {
						cells.add(r * columns + c);
					} else if (r === top || r === bottom || c === left || c === right) {
						stamp(c, r);
					}
				}
			}
			return cells;
		}

		// ellipse inscribed in the dragged bounding box; the +0.5 radius fudge
		// includes boundary cells for a fuller, better-connected curve
		const cx = (left + right) / 2;
		const cy = (top + bottom) / 2;
		const rx = (right - left) / 2 + 0.5;
		const ry = (bottom - top) / 2 + 0.5;
		const inside = (c: number, r: number) => {
			const nx = (c - cx) / rx;
			const ny = (r - cy) / ry;
			return nx * nx + ny * ny <= 1;
		};
		for (let r = top; r <= bottom; r++) {
			for (let c = left; c <= right; c++) {
				if (!inside(c, r)) continue;
				if (shapeFilled) {
					cells.add(r * columns + c);
				} else if (!(inside(c + 1, r) && inside(c - 1, r) && inside(c, r + 1) && inside(c, r - 1))) {
					stamp(c, r);
				}
			}
		}
		return cells;
	}

	// Method to reload background image when parameters change
	p.reloadBackgroundImage = (imageUrl: string | undefined) => {
		if (imageUrl) {
			backgroundImg = p.loadImage(imageUrl);
		} else {
			backgroundImg = undefined;
		}
	};

	// @ts-ignore
	window.editSketch = p;
};

export const displaySketch: Sketch = (p: P5CanvasInstance) => {
	const canvasWidth: number = parameters.width;
	const canvasHeight: number = parameters.height;
	let colours: Colour[][] = [];
	let backgroundColour: Colour = p.color(0);
	let backgroundImg: Image | undefined;

	p.preload = () => {
		if (parameters.background_image !== undefined && parameters.background_image !== null) {
			backgroundImg = p.loadImage(parameters.background_image);
		}
	};

	p.setup = function () {
		p.createCanvas(canvasWidth, canvasHeight);
		p.pixelDensity(1);
	};

	p.draw = function () {
		p.smooth();

		// Clear the canvas first to ensure transparency works correctly
		p.clear();

		//draw background
		p.background(backgroundColour);

		// show background image within square (scaled to fit canvas)
		if (backgroundImg) {
			// Apply grayscale and opacity filters
			p.push();
			if (parameters.background_grayscale && parameters.background_grayscale > 0) {
				// Apply grayscale filter using tint
				const grayValue = 255 - (parameters.background_grayscale / 100) * 255;
				p.tint(grayValue, grayValue, grayValue, (parameters.background_opacity || 1) * 255);
			} else {
				// Apply only opacity
				p.tint(255, 255, 255, (parameters.background_opacity || 1) * 255);
			}
			p.image(backgroundImg, 0, 0, canvasWidth, canvasHeight);
			p.pop();
		}

		//use 'for' loop to draw rows down the y axis
		for (var i = 0; i < colours.length; i++) {
			for (var j = 0; j < colours[i].length; j++) {
				if (colours[i][j] !== undefined) {
					drawBead(j, i, colours[i][j]);
				}
			}
		}
	};

	p.updateColours = (c: Colour[][], bc: Colour): void => {
		colours = c;
		backgroundColour = bc;
	};

	p.updateBackgroundImage = (imageUrl: string | undefined): void => {
		if (imageUrl !== undefined && imageUrl !== null) {
			backgroundImg = p.loadImage(imageUrl);
		} else {
			backgroundImg = undefined;
		}
	};

	function drawBead(colvalue: number, rowvalue: number, beadColour: Colour) {
		p.fill(beadColour);
		p.noStroke();

		p.rect(colvalue, rowvalue, 1, 1);
	}

	// Method to reload background image when parameters change
	p.reloadBackgroundImage = (imageUrl: string | undefined) => {
		if (imageUrl) {
			backgroundImg = p.loadImage(imageUrl);
		} else {
			backgroundImg = undefined;
		}
	};

	// @ts-ignore
	window.displaySketch = p;
};

export const calculateDrawSize = (bitmap_width: number, bitmap_height: number) => {
	let draw_size = 10;
	let canvas_width = canvasDimensions.width - 20; // Leave some margin
	let canvas_height = canvasDimensions.height - 20; // Leave some margin

	// expand or reduce
	if (bitmap_width * draw_size < canvas_width && bitmap_height * draw_size < canvas_height) {
		while (bitmap_width * draw_size < canvas_width && bitmap_height * draw_size < canvas_height) {
			draw_size = draw_size + 1;
		}
	} else if (bitmap_width * draw_size > canvas_width && bitmap_height * draw_size > canvas_height) {
		while (bitmap_width * draw_size > canvas_width && bitmap_height * draw_size > canvas_height) {
			draw_size = draw_size - 1;
		}
	}

	// half smaller so user can see the whole image
	draw_size = draw_size + 0.5;

	draw_size = Math.max(draw_size, 1);
	return draw_size;
};

export const rgbaToHexa = (rgba: string, forceRemoveAlpha = false) => {
	return (
		'#' +
		rgba
			.replace(/^rgba?\(|\s+|\)$/g, '') // Get's rgba / rgb string values
			.split(',') // splits them at ","
			.filter((string, index) => !forceRemoveAlpha || index !== 3)
			.map(string => parseFloat(string)) // Converts them to numbers
			.map((number, index) => (index === 3 ? Math.round(number * 255) : number)) // Converts alpha to 255 number
			.map(number => number.toString(16)) // Converts numbers to hex
			.map(string => (string.length === 1 ? '0' + string : string)) // Adds 0 when length of one number is 1
			.join('')
	); // Puts the array to together to a string
};

export const hexToRgba = (hex: string) => {
	let c: any;
	if (/^#([A-Fa-f0-9]{3,8})$/.test(hex)) {
		c = hex.substring(1).split('');
		if (c.length === 3) {
			c = [c[0], c[0], c[1], c[1], c[2], c[2]];
		}
		// Handle 8-digit hex (with alpha)
		let alpha = 1;
		if (c.length === 8) {
			alpha = parseInt(c[6] + c[7], 16) / 255;
			c = c.slice(0, 6);
		}
		c = '0x' + c.join('');
		return 'rgba(' + [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') + ',' + alpha + ')';
	}
	throw new Error('Bad Hex');
};

// return array of [r,g,b,a] from any valid color. if failed returns undefined
export const colorValues = (color: any) => {
	if (!color) return;
	if (color.toLowerCase() === 'transparent') return [0, 0, 0, 0];
	if (color[0] === '#') {
		if (color.length < 7) {
			// convert #RGB and #RGBA to #RRGGBB and #RRGGBBAA
			color = '#' + color[1] + color[1] + color[2] + color[2] + color[3] + color[3] + (color.length > 4 ? color[4] + color[4] : '');
		}
		return [parseInt(color.substr(1, 2), 16), parseInt(color.substr(3, 2), 16), parseInt(color.substr(5, 2), 16), color.length > 7 ? parseInt(color.substr(7, 2), 16) / 255 : 1];
	}
	if (color.indexOf('rgb') === -1) {
		// convert named colors
		var temp_elem = document.body.appendChild(document.createElement('fictum')); // intentionally use unknown tag to lower chances of css rule override with !important
		var flag = 'rgb(1, 2, 3)'; // this flag tested on chrome 59, ff 53, ie9, ie10, ie11, edge 14
		temp_elem.style.color = flag;
		if (temp_elem.style.color !== flag) return; // color set failed - some monstrous css rule is probably taking over the color of our object
		temp_elem.style.color = color;
		if (temp_elem.style.color === flag || temp_elem.style.color === '') return; // color parse failed
		color = getComputedStyle(temp_elem).color;
		document.body.removeChild(temp_elem);
	}
	if (color.indexOf('rgb') === 0) {
		if (color.indexOf('rgba') === -1) color += ',1'; // convert 'rgb(R,G,B)' to 'rgb(R,G,B)A' which looks awful but will pass the regxep below
		// eslint-disable-next-line no-useless-escape
		return color.match(/[\.\d]+/g).map(function (a) {
			return +a;
		});
	}
};

// return colorValues() value but alpha from 0 to 1 is converted to 0 to 255
export const p5ColorValues = (color: any) => {
	let arr = colorValues(color);
	if (arr) {
		if (arr.length === 4) {
			// Keep the alpha as is - p5.js can handle both 0-1 and 0-255 ranges
			// arr[3] = Math.round(arr[3] * 255);
		}
		return arr;
	}
};
